import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import AuthService from '../services/AuthService';
import ImageCacheAPIService from '../services/ImageCacheAPIService';

const AppContext = createContext();

export const useAppContext = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
    const [settings, setSettings] = useState({
        storeName: "Danés",
        logo: null,
        enableTips: true,
        enableComments: true,
        defaultView: 'products',
        button_color: '#009542',
        background_color: '#FAFAFA',
        primary_color: '#009542',
        secondary_color: '#6b7280',
        header_color: '#009542',
        card_color: '#ffffff',
        box_color: 'transparent',
    });

    const [sessionData, setSessionData] = useState(null);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [posCategories, setPosCategories] = useState([]); // pos.category con parent_id (jerarquía)
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [authenticatedUser, setAuthenticatedUser] = useState(null);
    const [orderType, setOrderType] = useState(null);

    const updateSettings = useCallback((newSettings) => {
        setSettings(prev => ({ ...prev, ...newSettings }));
        Object.entries(newSettings).forEach(([key, value]) => {
            if (['button_color', 'background_color', 'primary_color', 'secondary_color', 'header_color', 'card_color', 'box_color'].includes(key)) {
                document.documentElement.style.setProperty(`--${key}`, value);
                console.log(`Applied CSS variable --${key}: ${value}`);
            } else if (key === 'custom_store_name') {
                setSettings(prev => ({ ...prev, storeName: value }));
            } else if (key === 'logo_image') {
                if (value) {
                    let mimeType = 'image/jpeg';
                    if (value.startsWith('iVBORw0KGgo')) {
                        mimeType = 'image/png';
                    } else if (value.startsWith('/9j/')) {
                        mimeType = 'image/jpeg';
                    }
                    setSettings(prev => ({ ...prev, logo: `data:${mimeType};base64,${value}` }));
                } else {
                    setSettings(prev => ({ ...prev, logo: null }));
                }
            }
        });
    }, []);

    const updateSessionData = useCallback((data) => {
        console.log('╔═══════════════════════════════════════════════════════════╗');
        console.log('║         DATOS RECIBIDOS DE ODOO (LOGIN)                  ║');
        console.log('╚═══════════════════════════════════════════════════════════╝');
        console.log('sessionData completo:', JSON.stringify(data, null, 2));
        console.log('');
        console.log('Datos de empresa extraídos:');
        console.log('  - name:', data.company_data?.name);
        console.log('  - vat:', data.company_data?.vat);
        console.log('  - street:', data.company_data?.street);
        console.log('  - city:', data.company_data?.city);
        console.log('  - turn:', data.company_data?.turn);
        console.log('  - acteco:', data.company_data?.acteco);
        console.log('═══════════════════════════════════════════════════════════');

        setSessionData(data);
        setProducts(data.products || []);
        setCategories(data.categories || []);
        setPosCategories(data.pos_categories || []);
        setPaymentMethods(data.payment_methods || []);

        if (data?.products) {
            ImageCacheAPIService.cacheProducts(data.products)
                .then(result => {
                    console.log(`Cache API: ${result.success} imagenes guardadas en SQLite`);
                    if (result.errors > 0) {
                        console.warn(`Cache API: ${result.errors} imagenes fallaron`);
                    }
                })
                .catch(error => {
                    console.error('Error cacheando imagenes:', error);
                });
        }
    }, []);

    const updateAuthenticatedUser = useCallback((userData) => {
        console.log('Updating authenticated user:', userData);
        setAuthenticatedUser(userData);
        if (userData) {
            AuthService.saveUser(userData);
        }
    }, []);

    const clearAuthenticatedUser = useCallback(() => {
        console.log('Clearing authenticated user');
        setAuthenticatedUser(null);
        AuthService.logout();
    }, []);

    const clearSessionData = useCallback(() => {
        setSessionData(null);
        setProducts([]);
        setCategories([]);
        setPaymentMethods([]);
        clearAuthenticatedUser();
    }, [clearAuthenticatedUser]);

    useEffect(() => {
        const savedUser = AuthService.getUser();
        if (savedUser) {
            console.log('Usuario autenticado encontrado en localStorage:', savedUser);
            setAuthenticatedUser(savedUser);
        }
    }, []);

    useEffect(() => {
        console.log('Aplicando colores corporativos (tema rojo)...');
        Object.entries(settings).forEach(([key, value]) => {
            if (['button_color', 'background_color', 'primary_color', 'secondary_color', 'header_color', 'card_color', 'box_color'].includes(key)) {
                document.documentElement.style.setProperty(`--${key}`, value);
            }
        });
        console.log('Colores rojos aplicados');
    }, [settings]);

    return (
        <AppContext.Provider
            value={{
                settings,
                updateSettings,
                sessionData,
                posCategories,
                updateSessionData,
                clearSessionData,
                products,
                categories,
                paymentMethods,
                authenticatedUser,
                updateAuthenticatedUser,
                clearAuthenticatedUser,
                orderType,
                setOrderType,
            }}
        >
            {children}
        </AppContext.Provider>
    );
};